import React, { useState, useMemo } from 'react';
import { Calculator, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CuttingPlanCalculator = ({
  parts = [],
  boardWidth,
  boardHeight,
  kerf = 0.0025,
  onCalculationResult,
  onGenerate,
  isGenerating = false
}) => {
  const [calculated, setCalculated] = useState(false);
  const [result, setResult] = useState(null);

  const boardWidthMm = useMemo(() => (boardWidth || 2.75) * 1000, [boardWidth]);
  const boardHeightMm = useMemo(() => (boardHeight || 1.85) * 1000, [boardHeight]);
  const kerfMm = useMemo(() => (kerf || 0.0025) * 1000, [kerf]);

  const calculation = useMemo(() => {
    if (parts.length === 0) return null;

    const sheetArea = boardWidthMm * boardHeightMm;
    const totalPartsArea = parts.reduce((sum, p) => {
      const width = parseFloat(p.width) || 0;
      const height = parseFloat(p.height) || 0;
      const qty = parseInt(p.quantity) || 1;
      return sum + (width * height * qty);
    }, 0);

    const totalPartsCount = parts.reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);

    const estimatedSheets = Math.ceil((totalPartsArea * 1.15) / sheetArea);

    const sortedParts = [...parts].sort((a, b) => {
      const areaA = (parseFloat(a.width) || 0) * (parseFloat(a.height) || 0);
      const areaB = (parseFloat(b.width) || 0) * (parseFloat(b.height) || 0);
      return areaB - areaA;
    });

    const layout = [];
    let currentSheetNum = 1;
    let currentX = kerfMm;
    let currentY = kerfMm;
    let rowHeight = 0;

    for (const part of sortedParts) {
      const partWidth = parseFloat(part.width) || 0;
      const partHeight = parseFloat(part.height) || 0;
      const qty = parseInt(part.quantity) || 1;

      for (let i = 0; i < qty; i++) {
        if (currentX + partWidth + kerfMm > boardWidthMm) {
          currentX = kerfMm;
          currentY += rowHeight + kerfMm;
          rowHeight = 0;
        }

        if (currentY + partHeight + kerfMm > boardHeightMm) {
          currentSheetNum++;
          currentX = kerfMm;
          currentY = kerfMm;
          rowHeight = 0;
        }

        layout.push({
          partCode: part.partCode,
          partName: part.partName,
          width: partWidth,
          height: partHeight,
          x: currentX,
          y: currentY,
          rotation: 'A',
          sheetNumber: currentSheetNum
        });

        currentX += partWidth + kerfMm;
        rowHeight = Math.max(rowHeight, partHeight);
      }
    }

    const usedArea = layout.reduce((sum, p) => sum + (p.width * p.height), 0);
    const wasteArea = (sheetArea * currentSheetNum) - usedArea;
    const efficiency = ((usedArea / (sheetArea * currentSheetNum)) * 100).toFixed(1);

    const cuts = layout.length * 2 + (currentSheetNum - 1) * 2;
    const perimeter = layout.reduce((sum, p) => sum + (2 * (p.width + p.height)), 0) / 1000;

    return {
      totalParts: parts.length,
      totalQuantity: totalPartsCount,
      totalArea: (totalPartsArea / 1000000).toFixed(3),
      sheetArea: (sheetArea / 1000000).toFixed(3),
      estimatedSheets: currentSheetNum,
      actualSheets: currentSheetNum,
      usedArea: (usedArea / 1000000).toFixed(3),
      wasteArea: (wasteArea / 1000000).toFixed(3),
      efficiency,
      cuts,
      perimeter: perimeter.toFixed(2),
      layout,
      sheets: Array.from({ length: currentSheetNum }, (_, i) => ({
        sheetNumber: i + 1,
        width: boardWidthMm,
        height: boardHeightMm,
        pieces: layout.filter(p => p.sheetNumber === i + 1)
      }))
    };
  }, [parts, boardWidthMm, boardHeightMm, kerfMm]);

  const handleCalculate = () => {
    if (parts.length === 0) {
      toast.error('Adicione peças primeiro');
      return;
    }
    setResult(calculation);
    setCalculated(true);
    if (onCalculationResult) {
      onCalculationResult(calculation);
    }
  };

  const handleGenerate = () => {
    if (!calculated || !result) {
      toast.error('Calcule o plano primeiro');
      return;
    }
    if (onGenerate) {
      onGenerate();
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Configuração do Cálculo</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Dimensões da Chapa:</span>
            <span className="ml-2 font-medium text-blue-900">
              {boardWidthMm} x {boardHeightMm} mm
            </span>
          </div>
          <div>
            <span className="text-blue-700">Área da Chapa:</span>
            <span className="ml-2 font-medium text-blue-900">
              {calculation?.sheetArea || '0'} m²
            </span>
          </div>
          <div>
            <span className="text-blue-700">Kerf:</span>
            <span className="ml-2 font-medium text-blue-900">
              {kerfMm} mm
            </span>
          </div>
          <div>
            <span className="text-blue-700">Margem de Segurança:</span>
            <span className="ml-2 font-medium text-blue-900">15%</span>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <button
          onClick={handleCalculate}
          disabled={parts.length === 0}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calcular
        </button>

        {calculated && result && (
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <span className="animate-spin">⏳</span>
                Gerando...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Gerar Plano de Corte
              </>
            )}
          </button>
        )}
      </div>

      {result && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resultados do Cálculo</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{result.totalQuantity}</p>
                <p className="text-sm text-gray-500 mt-1">Total de Peças</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{result.actualSheets}</p>
                <p className="text-sm text-gray-500 mt-1">Chapas Necessárias</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">{result.efficiency}%</p>
                <p className="text-sm text-gray-500 mt-1">Aproveitamento</p>
              </div>

              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{result.cuts}</p>
                <p className="text-sm text-gray-500 mt-1">Total de Cortes</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Detalhes</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Área Total de Peças:</span>
                  <span className="font-medium">{result.totalArea} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Área Útil:</span>
                  <span className="font-medium">{result.usedArea} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Perímetro Total:</span>
                  <span className="font-medium">{result.perimeter} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Sobras:</span>
                  <span className="font-medium">{result.wasteArea} m²</span>
                </div>
              </div>
            </div>
          </div>

          {result.actualSheets > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Distribuição nas Chapas ({result.actualSheets} chapas)
              </h3>
              <div className="space-y-4">
                {result.sheets.map((sheet) => (
                  <div key={sheet.sheetNumber} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Chapa {sheet.sheetNumber}
                    </h4>
                    <p className="text-sm text-gray-500 mb-2">
                      {sheet.width} x {sheet.height} mm | {sheet.pieces.length} peças
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {sheet.pieces.map((piece, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-sm"
                        >
                          {piece.partCode} ({piece.width}x{piece.height})
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.efficiency < 60 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-900">Aproveitamento Baixo</h4>
                <p className="text-sm text-amber-700 mt-1">
                  O aproveitamento de {result.efficiency}% está abaixo do ideal (70%+).
                  Considere reorganizar as peças ou usar chapas de dimensões diferentes.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {!calculated && parts.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Adicione peças para calcular</p>
          <p className="text-sm text-gray-400">
            O sistema calculará quantas chapas são necessárias e o aproveitamento
          </p>
        </div>
      )}
    </div>
  );
};

export default CuttingPlanCalculator;
